import * as braze from '@braze/web-sdk';
import environment from '@lib/environment';

import appManager from '../../app/appManager';
import AppModule from '../../app/lib/AppModule';
import { AuthEvents, AuthUser } from '@modules/AuthModule/types';

export class BrazeModule extends AppModule {
  override preload(): void {
    // Initialize Braze when the app starts
    this.app.events.on('app.started', () => {
      if (environment.BRAZE_API_KEY && environment.BRAZE_SDK_ENDPOINT) {
        braze.initialize(environment.BRAZE_API_KEY, {
          baseUrl: environment.BRAZE_SDK_ENDPOINT, // e.g., 'sdk.iad-01.braze.com'
          enableLogging: environment.MODE !== 'production', // Debug in non-prod
        });

        this.app.logger.info('initialized');
      } else {
        this.app.logger.warn(
          'Braze not initialized: missing API key or SDK endpoint',
        );
      }
    });

    this.app.events.on(AuthEvents.SIGNIN, (user: AuthUser) => {
      if (environment.BRAZE_API_KEY && environment.BRAZE_SDK_ENDPOINT) {
        braze.changeUser(user.id);
        // Optionally set user properties
        const brazeUser = braze.getUser();
        if (brazeUser) {
          brazeUser.setCustomUserAttribute('displayName', user.name);
          brazeUser.setCustomUserAttribute('accountId', user.accountId);
          brazeUser.setCustomUserAttribute(
            'contactCenterId',
            user.contactCenterId,
          );
          brazeUser.setCustomUserAttribute('orgId', user.org.id);
          brazeUser.setCustomUserAttribute('orgName', user.org.name);
          if (user.email) {
            brazeUser.setEmail(user.email);
          }
        }

        this.app.logger.info('user identified: ', user.id);

        braze.automaticallyShowInAppMessages();
        // `openSession` should be called last - after `changeUser` and `automaticallyShowInAppMessages`
        braze.openSession();

        this.app.events.on(AuthEvents.SIGNOUT, () => {
          braze.destroy();
        });
      }
    });
  }

  private get initialized(): boolean {
    return !!braze.getUser()?.getUserId();
  }

  public logPageLoadedEvent(pathname: string): void {
    const guidRegex =
      /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
    const pathname_trim = pathname.replaceAll(guidRegex, 'id');
    if (
      environment.BRAZE_API_KEY &&
      environment.BRAZE_SDK_ENDPOINT &&
      this.initialized
    ) {
      braze.logCustomEvent('page-loaded', {
        path: pathname_trim,
      });
      this.app.logger.info(
        '"page-loaded" custom event properties: \n { path: "' +
          pathname_trim +
          '" } ',
      );
    }
  }
}

export default new BrazeModule(appManager.events, appManager.logger('Braze'));
